import { useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { useProviderStore } from '@/stores/providerStore';
import { useDeleteProvider } from '@/hooks/useProviders';
import { showToast } from '@/utils/toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Loader2 } from 'lucide-react';

export function ProviderActions() {
  const {
    selectedProvider,
    selectedProviderIds,
    isDeleteModalOpen,
    openCreateModal,
    closeDeleteModal,
    clearSelectedProviders,
    resetState
  } = useProviderStore();

  const deleteMutation = useDeleteProvider();

  const handleDelete = useCallback(async () => {
    try {
      if (selectedProvider) {
        await deleteMutation.mutateAsync(selectedProvider.id);
      } else if (selectedProviderIds.length > 0) {
        // Delete multiple providers
        for (const id of selectedProviderIds) {
          await deleteMutation.mutateAsync(id);
        }
      }

      showToast.success(selectedProvider 
        ? 'Provider deleted successfully'
        : `${selectedProviderIds.length} providers deleted successfully`);
      
      // Clear all states
      resetState();
      clearSelectedProviders();
      closeDeleteModal();
    } catch (error) {
      console.error('Delete error:', error);
      showToast.error(error instanceof Error ? error.message : 'Failed to delete provider(s)');
    }
  }, [selectedProvider, selectedProviderIds, deleteMutation, resetState, clearSelectedProviders, closeDeleteModal]);

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Providers</h1>
        <Button onClick={openCreateModal}>
          <Plus className="h-4 w-4 mr-2" />
          Add Provider
        </Button>
      </div>

      <AlertDialog open={isDeleteModalOpen} onOpenChange={closeDeleteModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>⚠️ Force Delete {selectedProviderIds.length > 1 ? 'Providers' : 'Provider'}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <div className="font-semibold text-destructive">
                  Warning: This action cannot be undone!
                </div>
                
                <div>This will permanently delete:</div>
                
                <div className="space-y-1">
                  <ul className="list-disc pl-4">
                    {selectedProvider ? (
                      <li>Provider "{selectedProvider.name}"</li>
                    ) : (
                      <li>{selectedProviderIds.length} selected providers</li>
                    )}
                    <li>All versions (including published versions)</li>
                    <li>All channels and their assignments</li>
                    <li>All plans and categories</li>
                    <li>All related data and history</li>
                  </ul>
                </div>
                
                <div className="font-medium">
                  Are you absolutely sure you want to proceed?
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Yes, Force Delete Everything'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
